import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, CheckCircle2, Users as UsersIcon, Pin, PinOff, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

// Colour the pct bar red → amber → green based on how the funnel is doing.
// Thresholds are rough — < 25% = red (leaking), 25-60% = amber, > 60% = green.
const colorFor = (pct) => {
  if (pct >= 60) return { bar: 'bg-emerald-500', text: 'text-emerald-300' };
  if (pct >= 25) return { bar: 'bg-amber-500', text: 'text-amber-300' };
  return { bar: 'bg-rose-500', text: 'text-rose-300' };
};

/**
 * OnboardingAnalytics — admin panel that surfaces the first-week funnel:
 * what % of new users hit each milestone. Lets the admin change the window
 * (7 / 30 / 90 d) so weekly reviews are easy.
 */
const OnboardingAnalytics = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-milestone in-flight action flag so the UI disables the right button
  // without blocking the others. Keyed by `<id>::<variant>` for promote and
  // `<id>::unpin` for unpin.
  const [pending, setPending] = useState(null);
  const [banner, setBanner] = useState(null); // { kind: 'success'|'error', text }

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/onboarding-analytics?window_days=${windowDays}`
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Auto-clear the inline banner after 4s so it doesn't camp on the page.
  useEffect(() => {
    if (!banner) return undefined;
    const id = setTimeout(() => setBanner(null), 4000);
    return () => clearTimeout(id);
  }, [banner]);

  const promote = useCallback(
    async (milestoneId, variant, force = false) => {
      const key = `${milestoneId}::${variant}`;
      setPending(key);
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/promote-variant`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            milestone_id: milestoneId,
            variant,
            force,
            window_days: windowDays,
          }),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          const detail = body?.detail || `HTTP ${r.status}`;
          // 400 from the min-sample/min-delta guard → offer to force.
          if (r.status === 400 && !force) {
            // eslint-disable-next-line no-alert
            const ok = window.confirm(`${detail}\n\nPromote anyway?`);
            if (ok) {
              setPending(null);
              return promote(milestoneId, variant, true);
            }
            setBanner({ kind: 'error', text: detail });
          } else {
            setBanner({ kind: 'error', text: detail });
          }
          return;
        }
        setBanner({
          kind: 'success',
          text: `Variant ${variant} promoted to 100% of new users for "${milestoneId}".`,
        });
        await reload();
      } catch (e) {
        setBanner({ kind: 'error', text: String(e.message || e) });
      } finally {
        setPending(null);
      }
    },
    [windowDays, reload]
  );

  const unpin = useCallback(
    async (milestoneId) => {
      const key = `${milestoneId}::unpin`;
      setPending(key);
      try {
        const r = await authFetch(
          `${BACKEND_URL}/api/admin/onboarding-variant-pins/${milestoneId}`,
          { method: 'DELETE' }
        );
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          setBanner({ kind: 'error', text: body?.detail || `HTTP ${r.status}` });
          return;
        }
        setBanner({
          kind: 'success',
          text: `Unpinned "${milestoneId}" — back to 50/50 A/B split.`,
        });
        await reload();
      } catch (e) {
        setBanner({ kind: 'error', text: String(e.message || e) });
      } finally {
        setPending(null);
      }
    },
    [reload]
  );

  return (
    <Card
      className="bg-slate-800/50 border-slate-700"
      data-testid="admin-onboarding-analytics"
    >
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-400" />
            Onboarding Conversion
          </CardTitle>
          <div className="flex items-center gap-1 rounded-lg bg-slate-900/60 border border-slate-700 p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                type="button"
                onClick={() => setWindowDays(w.days)}
                className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                  windowDays === w.days
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`onboarding-window-${w.days}d`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading && (
          <p className="text-sm text-gray-400">Loading analytics…</p>
        )}

        {!loading && error && (
          <p className="text-sm text-rose-300">
            Couldn't load analytics: {error}
          </p>
        )}

        {!loading && !error && data && (
          <>
            {/* Top summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4">
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                  <UsersIcon className="w-3.5 h-3.5" />
                  New users ({data.window_days}d)
                </div>
                <p
                  className="text-2xl font-bold text-white tabular-nums"
                  data-testid="onboarding-cohort-size"
                >
                  {data.cohort_size}
                </p>
              </div>
              <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4">
                <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  Fully onboarded
                </div>
                <p
                  className="text-2xl font-bold text-emerald-300 tabular-nums"
                  data-testid="onboarding-fully-pct"
                >
                  {data.fully_onboarded_pct}%
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {data.fully_onboarded} of {data.cohort_size} hit every milestone
                </p>
              </div>
            </div>

            {/* Inline success/error banner for pin actions */}
            {banner && (
              <div
                className={`mb-3 px-3 py-2 rounded-md text-xs ${
                  banner.kind === 'success'
                    ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-200'
                    : 'bg-rose-500/15 border border-rose-500/40 text-rose-200'
                }`}
                data-testid="onboarding-banner"
              >
                {banner.text}
              </div>
            )}

            {/* Per-milestone funnel */}
            <ul className="space-y-3">
              {data.milestones.map((m) => {
                const c = colorFor(m.pct);
                const v = m.variants || null;
                const pinnedArm = data?.pins?.[m.id]; // 'A' | 'B' | undefined
                const isPinned = pinnedArm === 'A' || pinnedArm === 'B';
                return (
                  <li
                    key={m.id}
                    className="rounded-lg bg-slate-900/40 border border-slate-700/60 p-3"
                    data-testid={`onboarding-milestone-${m.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">
                          {m.label}
                        </p>
                        {isPinned && (
                          <span
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-200 text-[9px] font-bold uppercase tracking-wider flex-shrink-0"
                            data-testid={`onboarding-pinned-${m.id}`}
                          >
                            <Pin className="w-2.5 h-2.5" />
                            {pinnedArm} pinned
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 tabular-nums">
                          {m.completed}/{data.cohort_size}
                        </span>
                        <span className={`text-sm font-bold tabular-nums ${c.text}`}>
                          {m.pct}%
                        </span>
                      </div>
                    </div>
                    <div
                      className="h-1.5 w-full rounded-full bg-slate-700/70 overflow-hidden"
                      role="progressbar"
                      aria-valuenow={m.pct}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      <div
                        className={`h-full rounded-full ${c.bar} transition-[width] duration-500`}
                        style={{ width: `${Math.max(2, m.pct)}%` }}
                      />
                    </div>

                    {/* A/B split — only render when we have variant data */}
                    {v && (v.A.exposed > 0 || v.B.exposed > 0) && (
                      <div
                        className="mt-2.5 grid grid-cols-2 gap-2"
                        data-testid={`onboarding-variants-${m.id}`}
                      >
                        {['A', 'B'].map((arm) => {
                          const stats = v[arm];
                          const isWinner = v.winner === arm;
                          const isArmPinned = pinnedArm === arm;
                          const pendingKey = `${m.id}::${arm}`;
                          return (
                            <div
                              key={arm}
                              className={`rounded-md border px-2.5 py-1.5 ${
                                isArmPinned
                                  ? 'bg-purple-500/10 border-purple-500/50'
                                  : isWinner
                                  ? 'bg-emerald-500/10 border-emerald-500/40'
                                  : 'bg-slate-900/60 border-slate-700/60'
                              }`}
                              data-testid={`onboarding-variant-${m.id}-${arm}`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className={`text-[10px] font-mono font-bold uppercase tracking-wider ${
                                  isArmPinned
                                    ? 'text-purple-200'
                                    : isWinner
                                    ? 'text-emerald-300'
                                    : 'text-gray-500'
                                }`}>
                                  Variant {arm}
                                  {isArmPinned && ' 📌'}
                                  {!isArmPinned && isWinner && ' ★'}
                                </span>
                                <span className={`text-xs font-bold tabular-nums ${
                                  isArmPinned
                                    ? 'text-purple-200'
                                    : isWinner
                                    ? 'text-emerald-300'
                                    : 'text-gray-300'
                                }`}>
                                  {stats.pct}%
                                </span>
                              </div>
                              <p className="text-[10px] text-gray-500 tabular-nums mb-1.5">
                                {stats.converted}/{stats.exposed} users
                              </p>
                              {/* Promote button — hidden on the pinned arm
                                  since promoting the current pin is a no-op */}
                              {!isArmPinned && (
                                <button
                                  type="button"
                                  onClick={() => promote(m.id, arm)}
                                  disabled={pending === pendingKey}
                                  className={`w-full text-[10px] font-semibold px-2 py-1 rounded transition-colors ${
                                    pending === pendingKey
                                      ? 'bg-slate-700 text-gray-500 cursor-wait'
                                      : 'bg-indigo-600/70 hover:bg-indigo-500 text-white'
                                  }`}
                                  data-testid={`onboarding-promote-${m.id}-${arm}`}
                                >
                                  <Trophy className="w-2.5 h-2.5 inline mr-0.5" />
                                  {pending === pendingKey ? 'Promoting…' : `Promote ${arm}`}
                                </button>
                              )}
                              {isArmPinned && (
                                <button
                                  type="button"
                                  onClick={() => unpin(m.id)}
                                  disabled={pending === `${m.id}::unpin`}
                                  className={`w-full text-[10px] font-semibold px-2 py-1 rounded transition-colors ${
                                    pending === `${m.id}::unpin`
                                      ? 'bg-slate-700 text-gray-500 cursor-wait'
                                      : 'bg-purple-600/70 hover:bg-purple-500 text-white'
                                  }`}
                                  data-testid={`onboarding-unpin-${m.id}`}
                                >
                                  <PinOff className="w-2.5 h-2.5 inline mr-0.5" />
                                  {pending === `${m.id}::unpin` ? 'Unpinning…' : 'Unpin'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {v && v.winner === 'insufficient' && (v.A.exposed > 0 || v.B.exposed > 0) && !isPinned && (
                      <p className="text-[10px] text-gray-500 italic mt-1">
                        Need ≥ 5 users per variant to declare a winner. ≥ 50 / arm + ≥ 10% delta to auto-promote (override available).
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>

            {data.cohort_size === 0 && (
              <p className="text-xs text-gray-500 mt-4 text-center">
                No new signups in the last {data.window_days} days — widen the window or check back after some sign-ups.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OnboardingAnalytics;
